import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const gatherRequirementsSkill: Skill = {
    name: SKILL_NAMES.gatherRequirements,
    description:
        'Load BEFORE creating any new video or asking multiple questions for how to collect the brief.',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Gather Requirements First

Before building a new video, open with ONE questionnaire that collects the whole
brief at once. Everything downstream — template, script, media, pacing — is
expensive to redo, so a single round of questions up front is far cheaper than
guessing and rebuilding.

## The rules that matter
- Call \`${TOOL_NAMES.askUserQuestion}\` once with all questions together.
- Ask before \`${TOOL_NAMES.createBlankVideo}\` to show all the questions as a full-pane questionnaire.
- Do not ask about things the user has explicitly specified, unless what they
  asked for isn't possible (such as a duration over the limit).
- Research before you ask. If the user gave a topic or script, search media and
  sources first to confirm you can source what it needs. Ask about content you
  cannot find.
- Keep questions to 10 words or fewer.
- Do NOT add a "Decide for me" / "Surprise me" choice. Every question is
  already skippable.
- Do NOT write your own "Other", "Something else" or "None of the above"
  choice. Radio and checkbox questions get a free-text "Other" row
  automatically, and yours is dropped as a duplicate.

## Say what's still open
The questionnaire should read like it came from someone who thought about the
request, not a generic form. When it helps, say so:
- \`intro\`: 1-2 first-person sentences about the overall video, or an issue,
  dilemma or choice that spans several of the questions. Don't recap the
  user's request back to them. E.g. "Let's make a short, high energy promo for
  Lumen5!" or "Your request covers three features, but 30 seconds can only
  fit one."
- A question's \`description\`: one short statement on why you need the
  answer, tied to this request. Don't ask a second question in it. The most
  common case is asking for something you couldn't find. E.g. "I couldn't find
  your logo.", "No stock footage shows your product, so I'll need a few photos
  of it." or "Coffee is broad. I can't tell if this is for a café, a bean
  brand, or general interest."

Both are optional. Most questions need no description. Skip either one when it
would only restate the question or say something that fits any video — generic
filler makes the form longer without making it smarter.

## Script details
If the user doesn't provide a script or storyboard to use, ask questions to help you write the script:
- What's the core story? Ask a radio question with a few suggested angles
- Who's the audience?
- Any specific numbers or claims you want to include?
- Duration

Pasted descriptive copy (a product sheet, spec list, article, notes) is source
material, not a script to use. If it's unclear whether the user wants their text
read as-is, add one radio question to this questionnaire: "Use my text as-is" /
"Tighten it into a script".

## Ask only what the context hasn't already answered
A questionnaire is for the decisions that are genuinely open. Before adding a
question, check whether the context already settles it — an attachment, a
blueprint, or the account's own setup often does. When it does, skip the question
and proceed on that answer. Skipping is silent: don't ask, and don't replace it
with a "just confirming" question.

- Aspect ratio: ask when nothing implies one. Skip it when the user attached a
  recording the video is built around (a talking head, or footage that carries the
  story) and match its stated \`ASPECT_RATIO\` (given as width:height, e.g. \`1.78:1\`)
  to the closest supported video ratio: about 1.78 → 16:9, about 1.0 → 1:1 (square),
  about 0.8 → \`fourfive\` (4:5), about 0.56 → 9:16 (vertical); anything wider than
  16:9 still maps to 16:9. A blueprint or reference video the user pointed at also
  fixes the aspect ratio; don't ask then either.
- Template: offer a choice only when there's a real one. Skip the question and
  just use the template when a blueprint or reference video already implies the
  look, or when the account effectively has a single usable template — a lone
  custom template with no public catalog beside it, which is how a restricted
  enterprise workspace shows up here. When the choice IS real, use
  \`type: "template"\` and offer a few suggested templates in \`template_ids\`.
- Voiceover: what voice to use, if any. Skip it when the user attached a voiceover
  recording, or the narration is otherwise already decided. Otherwise use
  \`type: "voice"\`. Call
  \`${TOOL_NAMES.searchVoices}\` FIRST to find voices matching the content's language and tone,
  then pass their \`slug\` values in \`voice_slugs\` — one entry per voice. These must be
  slugs, not descriptions: an unknown id is dropped and the user sees generic
  defaults instead. Omit \`voice_slugs\` only if the search fails.
  If the search resolved a language, also set the question's \`language\` to that
  exact value — a multi-language voice's preview otherwise plays an arbitrary
  sample instead of one in the language it was actually suggested for.
  Set \`require_voiceover: true\` when the format you're building cannot exist
  without narration, so the question stops offering "No voiceover" — an answer
  that would contradict the video the user asked for. Leave it off (the default)
  whenever going without a voiceover is a real choice, and the skill you're
  following says which of the two applies.

## Captions — the one question to ask AFTER the video exists
Caption designs belong to a template, so the tiles can only preview once the
video's template is actually set. Ask this one on its own, after
\`${TOOL_NAMES.createVideo}\` (or whichever create tool you used), not in the
up-front questionnaire — asked before, there is no template to preview and the
user sees an empty picker.

Ask it ONLY when the recipe you are following says it will enable captions, or
the user asked for them. Most recipes deliberately keep captions off (the
graphics or on-screen text do that job, or the design renders its own), and
asking there invites the user to turn on something the format is built to go
without. Use \`type: "caption"\`, then apply the answer with
\`${TOOL_NAMES.setCaptions}\`: a picked design answers with its id, so pass that
as \`design_id\`. An answer of "No captions" is the user turning them off — leave
captions off rather than looking for a design to apply.

## Ask for content you can't source yourself
Search first, then ask only for what the search couldn't produce. The build is
only as good as the material behind it, so ask if the user has content to use:
- **Media** — the most common gap. A specific brand, product, person, or niche
  topic rarely has usable stock footage. If a media search comes back with
  nothing that actually shows the subject, ask the user to upload their own
  images or clips rather than building the video around generic substitutes.
  Use \`type: "file_upload"\`
- If the user asks for a video that requires a recorded voiceover or footage but doesn't provide,
    ask for it. Use \`type: "file_upload"\`
- **Brand** — \`type: "brand"\` when the video seems like it should be branded, to let
  the user pick one of their saved brand kits. It only offers kits they already
  have, so skip it when the workspace has none.
- **Call to action** — when the video seems to needs to end on one (a URL, an
  offer, a next step) and the user hasn't said what it is. Suggest a few options.

## User dismissal
If the user dismissed the questionnaire without answering, don't re-ask and proceed on
your best judgment.`;
    },
};
